import { EyeIcon, MoreHorizontalIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { deleteCustomer, restoreCustomer, type Customer } from '@/api/customers'
import { useApiErrorMessage } from '@/api/errors'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Opening the details is for everybody; deleting and restoring are an administrator's, because
 * they take a company out of — or back into — everybody's list.
 *
 * Deleting asks first even though it is soft: the record disappears from every list, which looks
 * the same as a hard delete to whoever is looking for it.
 */
export function CustomerRowActions({
  customer,
  canDelete,
  onShowDetails,
  onChanged,
}: {
  customer: Customer
  canDelete: boolean
  onShowDetails: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  async function run(action: () => Promise<unknown>, success: string, failure: string) {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      await action()
      toast.success(success)
      onChanged()
    } catch (error) {
      toast.error(errorMessage(error, failure))
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('customers.actions.menu', { name: customer.companyName })}
          >
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onSelect={onShowDetails}>
            <EyeIcon />
            {t('customers.actions.details')}
          </DropdownMenuItem>
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              {customer.deleted ? (
                <DropdownMenuItem
                  onSelect={() =>
                    void run(
                      () => restoreCustomer(customer.id),
                      t('customers.restore.success', { name: customer.companyName }),
                      t('customers.restore.failed'),
                    )
                  }
                >
                  <RotateCcwIcon />
                  {t('customers.actions.restore')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem variant="destructive" onSelect={() => setConfirming(true)}>
                  <Trash2Icon />
                  {t('customers.actions.delete')}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('customers.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('customers.delete.description', { name: customer.companyName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() =>
                void run(
                  () => deleteCustomer(customer.id),
                  t('customers.delete.success', { name: customer.companyName }),
                  t('customers.delete.failed'),
                )
              }
            >
              {t('customers.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
